/**
 * Routes des niveaux Organisation, Programme et Plateforme (lot U6b, vague 8).
 *
 * Module séparé de `api` (client.ts) : la surface caractérisée du client n'est pas modifiée.
 * Formes lues dans les contrôleurs de la passerelle (l'OpenAPI les déclare non typées) :
 * - `ProgramMemberService.lister` / `ajouter` / `changerRole` / `retirer` ;
 * - `PortfolioService.train` (besoin d'en connaître : un lecteur ne voit que ses projets, le reste
 *   est un COMPTE, jamais un nom) ;
 * - `OperationsHealthController#gpuQueue` (super-administrateur) ;
 * - `ProgramSettingsService.lire` / `modifier` (SRF-PGM-008) et `ProgramInitiativeService.lister`
 *   (SRF-PGM-015 : besoin d'en connaître, les initiatives des projets non ouverts sont COMPTÉES).
 * - Lot P 0.5.x : `CapaciteProgrammeService` (SRF-PGM-011, coût et capacité : estimé et consommé en
 *   deux colonnes, jamais additionnés), `ProgrammeObjectifService` (SRF-PGM-010 objectif, jalon cible,
 *   registre des risques ; SRF-PGM-004 objectif d'une initiative lancée).
 */
import { ApiError, request } from './http'
import { ajouterEnDeuxActes, profilsDuRole, siVersionLue } from './v2/rolesVersProfils'

export type RoleProgramme = 'owner' | 'contributor' | 'viewer'

export interface MembreProgramme {
  user_id: string
  username: string | null
  role: RoleProgramme | string
  joined_at: string | null
}

export interface VersionTrain {
  id: string | number
  version: string
  title: string | null
  status: string
  created_at: string | null
  released_at: string | null
}

export interface ProjetTrain {
  workspace_id: string
  name: string
  status: string | null
  versions: VersionTrain[]
  /** D-08 : dernière version au statut `released` (« Déclarer livrée »), parmi TOUTES les versions. */
  derniere_version_livree: VersionTrain | null
  /** D-08 : dernier déploiement réussi (hors retour arrière), livrée ou non ; absent d'une passerelle antérieure. */
  derniere_version_deployee?: VersionDeployee | null
  version_en_preparation: VersionTrain | null
}

export interface VersionDeployee {
  id: string | number
  version: string
  status: string | null
  environnement: string
  deployee_le: string | null
}

export interface TrainProgramme {
  program_id: string
  /** `pilote` = propriétaire ou contributeur du programme ; `lecteur` sinon. */
  acces: 'pilote' | 'administrateur' | 'lecteur' | string
  projets: ProjetTrain[]
  projets_non_accessibles: number
  projets_non_accessibles_libelle: string | null
}

export interface TacheFileGpu {
  rank: number
  state: string
  tenant_name: string | null
  kind: string
  card: string | null
  wait_seconds: number | null
}

export interface FileGpu {
  checked_at: string
  running: number
  queued: number
  truncated: boolean
  jobs: TacheFileGpu[]
}

export type StatutProgramme = 'active' | 'inactive' | 'suspended'

export interface ReglagesProgramme {
  program_id: string
  name: string
  description: string | null
  organisation_id: string
  owner: string | null
  status: StatutProgramme | string
  template_id: string | null
  template_name: string | null
  created_at: string | null
  updated_at: string | null
  /** Jeton de version : renvoyé en `expected_version` pour détecter une modification concurrente. */
  version: string
  proprietaires: { user_id: string; username: string | null }[]
  /** Propriétaire du programme ou administrateur de la plateforme. */
  peut_modifier: boolean
  statuts: string[]
}

export interface ModificationReglages {
  name?: string
  description?: string
  status?: string
  /** Chaîne vide : retour au gabarit Default. */
  template_id?: string
  expected_version?: string
}

export interface InitiativeLancee {
  id: string
  chemin: 'epics' | 'cadrage' | string
  titre: string | null
  auteur: string | null
  auteur_nom: string | null
  workspace_id: string
  workspace_name: string
  job_id: string | null
  document_id: string | null
  lance_le: string | null
  /** `pending`, `processing`, `done`, `failed` ; `null` si le job est introuvable (purgé). */
  job_statut: string | null
  job_code: string | null
  resultat: { epics?: number; epics_a_accepter?: number; approbation?: string | null }
  /** SRF-PGM-004 (0.5.x) — objectif saisi par l'auteur ou un pilote ; absent d'une passerelle antérieure. */
  objectif?: string | null
  /** Statut CALCULÉ par la passerelle (jamais stocké) ; un job échoué laisse l'initiative `proposee`. */
  statut?: StatutInitiative | string | null
  /** Epics réels (non refusés) terminés sur retenus ; `null` pour un cadrage. */
  avancement?: { epics_termines: number; epics_total: number } | null
}

export type StatutInitiative = 'en_cours' | 'terminee' | 'en_revue' | 'cadree' | 'proposee'

export interface TraceInitiatives {
  program_id: string
  acces: 'pilote' | 'administrateur' | 'lecteur' | string
  initiatives: InitiativeLancee[]
  initiatives_non_accessibles: number
}

// ------------------------------------------------------------------ SRF-PGM-011 coût et capacité

export interface MissionCapacite {
  mission_id: string
  title: string | null
  status: string
  suspended_reason: string | null
}

export interface ProjetCapacite {
  workspace_id: string
  name: string
  active_missions: number
  missions: MissionCapacite[]
  /** Reste à faire estimé des missions actives ; `null` = aucune estimation (« — », jamais 0). */
  estimated_minutes: number | null
  consumed_minutes: number
}

export interface CapaciteProgramme {
  program_id: string
  /** AAAA-MM (UTC). */
  month: string
  acces: 'pilote' | 'administrateur' | 'membre' | string
  status: 'nominal' | 'vide' | 'sans_projet' | string
  projects: ProjetCapacite[]
  /** Besoin d'en connaître : les projets non ouverts, en UNE ligne sans nom. */
  not_accessible: { count: number; consumed_minutes: number; estimated_minutes: number | null; label: string | null }
  totals: { projects: number; active_missions: number; consumed_minutes: number; estimated_minutes: number | null }
}

// ------------------------------------------------------------------ SRF-PGM-010 objectif, jalon, risques

export type ImpactRisque = 'low' | 'medium' | 'high'

export interface RisqueProgramme {
  id: number | string
  title: string
  impact: ImpactRisque | string
  owner_user_id: string | null
  owner_name: string | null
  mitigation: string | null
  status: 'open' | 'closed' | string
  created_by: string | null
  created_at: string | null
  closed_at: string | null
}

export interface ObjectifProgramme {
  program_id: string
  objective: string | null
  target_milestone: string | null
  /** AAAA-MM-JJ. */
  target_date: string | null
  /** Jeton de version ; `null` tant que l'objectif n'a jamais été enregistré. */
  version: string | null
  updated_by: string | null
  updated_by_name: string | null
  risks: RisqueProgramme[]
  open_risks: number
  /** Pilote du programme (propriétaire ou contributeur) ou administrateur de la plateforme. */
  peut_modifier: boolean
  statut: 'vide' | 'nominal' | string
}

/** Remplace l'objectif, le jalon et la date (un champ vide les efface). */
export interface ModificationObjectif {
  objective: string | null
  target_milestone: string | null
  target_date: string | null
  expected_version?: string
}

export interface NouveauRisque {
  title: string
  impact: ImpactRisque
  owner_user_id?: string
  mitigation?: string
}

/** Champs absents inchangés ; `owner_user_id: ''` = aucun porteur. */
export interface ModificationRisque {
  title?: string
  impact?: ImpactRisque
  owner_user_id?: string
  mitigation?: string
  status?: 'open' | 'closed'
}

const p = (id: string): string => `/programs/${encodeURIComponent(id)}`

export const niveauxApi = {
  membresProgramme: (id: string) => request<MembreProgramme[]>(`${p(id)}/members`),
  // Retrait de l'historique (D2, R2) : arrivée en lecteur, puis profils traduits du rôle choisi (If-Match sur la version lue).
  // Deux actes explicites (critique codex t1, constat 9) : un second acte en échec rend AjoutPartielError (membre en lecteur).
  ajouterMembreProgramme: (id: string, userId: string, role: RoleProgramme) =>
    ajouterEnDeuxActes(
      () => request<MembreProgramme>(`${p(id)}/members`, { method: 'POST', body: JSON.stringify({ user_id: userId }) }),
      role === 'viewer' ? null : () => niveauxApi.changerRoleProgramme(id, userId, role)),
  changerRoleProgramme: async (id: string, userId: string, role: RoleProgramme) => {
    const membres = await request<Array<{ user_id: string; version?: string }>>(`${p(id)}/members`)
    const version = membres.find((m) => m.user_id === userId)?.version
    return request<MembreProgramme>(`${p(id)}/members/${encodeURIComponent(userId)}`, {
      method: 'PUT', headers: siVersionLue(version), body: JSON.stringify({ profils: profilsDuRole('programme', role) }),
    })
  },
  retirerMembreProgramme: (id: string, userId: string) =>
    request<{ removed: boolean }>(`${p(id)}/members/${encodeURIComponent(userId)}`, { method: 'DELETE' }),
  trainProgramme: (id: string) => request<TrainProgramme>(`${p(id)}/train`),
  reglagesProgramme: (id: string) => request<ReglagesProgramme>(`${p(id)}/settings`),
  modifierReglagesProgramme: (id: string, modification: ModificationReglages) =>
    request<ReglagesProgramme>(p(id), { method: 'PATCH', body: JSON.stringify(modification) }),
  initiativesProgramme: (id: string) => request<TraceInitiatives>(`${p(id)}/initiatives`),
  fileGpu: () => request<FileGpu>('/admin/operations/gpu-queue'),
  capaciteProgramme: (id: string, mois?: string | null) =>
    request<CapaciteProgramme>(`${p(id)}/capacity${mois ? `?month=${encodeURIComponent(mois)}` : ''}`),
  objectifProgramme: (id: string) => request<ObjectifProgramme>(`${p(id)}/objective`),
  modifierObjectifProgramme: (id: string, modification: ModificationObjectif) =>
    request<ObjectifProgramme>(`${p(id)}/objective`, { method: 'PUT', body: JSON.stringify(modification) }),
  ajouterRisque: (id: string, risque: NouveauRisque) =>
    request<ObjectifProgramme>(`${p(id)}/risks`, { method: 'POST', body: JSON.stringify(risque) }),
  modifierRisque: (id: string, riskId: number | string, modification: ModificationRisque) =>
    request<ObjectifProgramme>(`${p(id)}/risks/${encodeURIComponent(String(riskId))}`, {
      method: 'PATCH', body: JSON.stringify(modification),
    }),
  /** `ref` = le champ `id` de la trace : `job_id` d'un lancement « epics », `document_id` d'un cadrage. */
  objectifInitiative: (id: string, ref: string, objective: string) =>
    request<{ program_id: string; initiative_ref: string; objective: string }>(
      `${p(id)}/initiatives/${encodeURIComponent(ref)}/objective`, { method: 'PUT', body: JSON.stringify({ objective }) }),
}

/** Code métier d'une erreur de la passerelle (`PROGRAM_LAST_OWNER`…), lu dans le corps JSON. */
export function codeErreur(e: unknown): string | null {
  if (!(e instanceof ApiError) || !e.detail) return null
  try {
    const corps = JSON.parse(e.detail) as { code?: unknown }
    return typeof corps.code === 'string' ? corps.code : null
  } catch {
    return null
  }
}

/** Un refus d'accès (403) se dit comme un refus, jamais comme une panne. */
export function estRefus(e: unknown): boolean {
  return e instanceof ApiError && e.status === 403
}
